/**
 * AlertMessage - Reusable alert component for error and success messages
 * Eliminates duplication across 15+ files
 */

import React from 'react';
import { Alert, AlertProps } from '@mui/material';

export interface AlertMessageProps {
  /** Alert message to display */
  message: string | null;

  /** Alert severity */
  severity?: AlertProps['severity'];

  /** Callback when alert is dismissed */
  onClose?: () => void;

  /** Additional MUI Alert props */
  alertProps?: Omit<AlertProps, 'severity' | 'onClose' | 'children'>;
}

/**
 * Displays an alert message with consistent styling
 * Automatically hides when message is null
 */
export const AlertMessage: React.FC<AlertMessageProps> = ({
  message,
  severity = 'info',
  onClose,
  alertProps = {},
}) => {
  if (!message) {
    return null;
  }

  return (
    <Alert
      severity={severity}
      onClose={onClose}
      sx={{ mb: 2, ...alertProps.sx }}
      {...alertProps}
    >
      {message}
    </Alert>
  );
};

/**
 * Convenience components for common use cases
 */
export const ErrorAlert: React.FC<Omit<AlertMessageProps, 'severity'>> = (props) => (
  <AlertMessage severity="error" {...props} />
);

export const SuccessAlert: React.FC<Omit<AlertMessageProps, 'severity'>> = (props) => (
  <AlertMessage severity="success" {...props} />
);

export const WarningAlert: React.FC<Omit<AlertMessageProps, 'severity'>> = (props) => (
  <AlertMessage severity="warning" {...props} />
);

export const InfoAlert: React.FC<Omit<AlertMessageProps, 'severity'>> = (props) => (
  <AlertMessage severity="info" {...props} />
);
